import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "selectAll", "bulkBar", "count", "row"]
  static values = {
    adminId: Number,
    composerFrame: String,
    requiresSavedList: Boolean,
    selectedUrl: String,
    claimsUrl: String,
    streamSourceId: String
  }

  connect() {
    this.contactUpdatesActive = true
    this.claimExpiries = new Map()
    this.claimsChanged = this.claimsChanged.bind(this)
    this.visibilityChanged = this.visibilityChanged.bind(this)
    this.connectionChanged = this.connectionChanged.bind(this)
    this.syncClaims = this.syncClaims.bind(this)

    document.addEventListener("admin-marketing:claims-changed", this.claimsChanged)
    document.addEventListener("visibilitychange", this.visibilityChanged)
    window.addEventListener("online", this.syncClaims)

    this.observeStreamConnection()
    this.rowTargets.forEach((row) => this.rememberClaimExpiry(row.dataset.parentId, row.dataset.claimExpiresAt))
    this.scheduleExpirySync()
    this.refreshContactTimes()
    this.scheduleContactRefresh()
    this.refresh()
  }

  disconnect() {
    this.contactUpdatesActive = false
    document.removeEventListener("admin-marketing:claims-changed", this.claimsChanged)
    document.removeEventListener("visibilitychange", this.visibilityChanged)
    window.removeEventListener("online", this.syncClaims)
    this.streamObserver?.disconnect()
    this.claimSyncAbort?.abort()
    window.clearTimeout(this.expiryTimer)
    window.clearTimeout(this.contactTimer)
  }

  changed(event) {
    event.stopPropagation()
    event.currentTarget.closest("tr")?.classList.toggle("is-selected", event.currentTarget.checked)
    this.refresh()
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked
    this.checkboxTargets.filter((box) => !box.disabled).forEach((box) => {
      box.checked = checked
      box.closest("tr")?.classList.toggle("is-selected", checked)
    })
    this.refresh()
  }

  clear() {
    this.checkboxTargets.forEach((box) => {
      box.checked = false
      box.closest("tr")?.classList.remove("is-selected")
    })
    if (this.hasSelectAllTarget) this.selectAllTarget.checked = false
    this.refresh()
  }

  stop(event) {
    event.stopPropagation()
  }

  openRow(event) {
    if (event.target.closest("input, button, a, label")) return
    if (event.currentTarget.dataset.claimed === "true") return
    this.loadComposer(event.currentTarget.dataset.composerUrl)
  }

  openComposer(event) {
    if (event.currentTarget.disabled || this.element.dataset.filtersPending === "true") return
    this.selectAvailableVisible()
    this.loadComposer(event.currentTarget.dataset.composerUrl)
  }

  openSelected() {
    if (this.element.dataset.filtersPending === "true") return
    const url = new URL(this.selectedUrlValue, window.location.origin)
    this.selectedIds().forEach((id) => url.searchParams.append("recipient_ids[]", id))
    this.loadComposer(`${url.pathname}${url.search}`)
  }

  refresh() {
    const count = this.selectedIds().length
    const availableCount = this.checkboxTargets.filter((box) => !box.disabled).length
    if (this.hasCountTarget) this.countTarget.textContent = count.toLocaleString("en-CA")
    if (this.hasBulkBarTarget) this.bulkBarTarget.hidden = count === 0
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.disabled = availableCount === 0
      this.selectAllTarget.checked = count > 0 && count === availableCount
      this.selectAllTarget.indeterminate = count > 0 && count < availableCount
    }
  }

  selectedIds() {
    return this.checkboxTargets.filter((box) => box.checked && !box.disabled).map((box) => box.value)
  }

  selectAvailableVisible() {
    this.checkboxTargets.filter((box) => !box.disabled).forEach((box) => {
      box.checked = true
      box.closest("tr")?.classList.add("is-selected")
    })
    this.refresh()
  }

  async syncClaims() {
    if (!this.hasClaimsUrlValue || this.rowTargets.length === 0) return

    this.claimSyncAbort?.abort()
    this.claimSyncAbort = new AbortController()
    const url = new URL(this.claimsUrlValue, window.location.origin)
    this.rowTargets.forEach((row) => url.searchParams.append("parent_ids[]", row.dataset.parentId))

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        cache: "no-store",
        signal: this.claimSyncAbort.signal
      })
      if (!response.ok) throw new Error(`Claim sync failed (${response.status})`)
      const payload = await response.json()
      this.applyClaims(payload.claims || {})
      this.applyContactedAt(payload.contacted_at || {})
    } catch (error) {
      // A transient sync failure must not interrupt the directory itself.
      if (error.name !== "AbortError") {
        window.clearTimeout(this.expiryTimer)
        this.expiryTimer = window.setTimeout(this.syncClaims, 30_000)
      }
    }
  }

  applyClaims(claims) {
    this.rowTargets.forEach((row) => {
      const claim = claims[row.dataset.parentId]
      this.applyClaimToRow(row, claim)
    })
    this.scheduleExpirySync()
    this.refresh()
  }

  claimsChanged(event) {
    const claims = event.detail?.claims || {}
    const releasedIds = new Set((event.detail?.releasedIds || []).map(String))

    this.rowTargets.forEach((row) => {
      const parentId = row.dataset.parentId
      if (claims[parentId]) this.applyClaimToRow(row, claims[parentId])
      if (releasedIds.has(parentId)) this.applyClaimToRow(row, null)
    })

    this.applyContactedAt(event.detail?.contactedAt || {})
    this.scheduleExpirySync()
    this.refresh()
  }

  applyClaimToRow(row, claim) {
    const claimed = Boolean(claim) && (Number(claim.account_id) !== this.adminIdValue || claim.campaign_id != null)
    row.dataset.claimed = claimed ? "true" : "false"
    row.dataset.claimExpiresAt = claim?.expires_at || ""
    row.classList.toggle("is-claimed", claimed)
    this.rememberClaimExpiry(row.dataset.parentId, claim?.expires_at)

    const checkbox = row.querySelector("input[type='checkbox']")
    if (checkbox) {
      checkbox.disabled = claimed
      if (claimed) checkbox.checked = false
    }

    const working = row.querySelector(".admin-marketing-working")
    if (working) {
      working.hidden = !claim
      const label = working.querySelector("[data-working-label]")
      if (label && claim) label.textContent = `${claim.owner_name} is working on this`
    }
    if (claimed) row.classList.remove("is-selected")
  }

  applyContactedAt(contactedAt) {
    this.rowTargets.forEach((row) => {
      const value = contactedAt[row.dataset.parentId]
      if (!value) return

      const contact = row.querySelector(".admin-marketing-contact")
      if (!contact || !Number.isFinite(Date.parse(value))) return
      contact.dataset.lastContactedAt = value
    })
    this.refreshContactTimes()
    this.scheduleContactRefresh()
  }

  refreshContactTimes() {
    const now = Date.now()
    this.rowTargets.forEach((row) => {
      const contact = row.querySelector(".admin-marketing-contact")
      const timestamp = Date.parse(contact?.dataset.lastContactedAt || "")
      if (!contact || !Number.isFinite(timestamp)) return

      const seconds = Math.max(0, Math.floor((now - timestamp) / 1_000))
      let label = "Just now"
      if (seconds >= 60) {
        const [divisor, unit] = seconds < 3_600 ? [60, "minute"] : seconds < 86_400 ? [3_600, "hour"] : [86_400, "day"]
        const count = Math.floor(seconds / divisor)
        label = `${count.toLocaleString("en-CA")} ${unit}${count === 1 ? "" : "s"} ago`
      }
      contact.textContent = label
      contact.classList.toggle("cold", seconds >= 90 * 86_400)
    })
  }

  scheduleContactRefresh() {
    window.clearTimeout(this.contactTimer)
    if (!this.contactUpdatesActive || document.visibilityState === "hidden") return
    if (!this.rowTargets.some((row) => row.querySelector(".admin-marketing-contact")?.dataset.lastContactedAt)) return

    // Only update text locally; do not poll the server to age these labels.
    this.contactTimer = window.setTimeout(() => {
      this.refreshContactTimes()
      this.scheduleContactRefresh()
    }, 60_000)
  }

  observeStreamConnection() {
    if (!this.hasStreamSourceIdValue) {
      this.syncClaims()
      return
    }

    this.streamSource = document.getElementById(this.streamSourceIdValue)
    if (!this.streamSource) {
      this.syncClaims()
      return
    }

    this.streamObserver = new MutationObserver(this.connectionChanged)
    this.streamObserver.observe(this.streamSource, { attributes: true, attributeFilter: ["connected"] })
    if (this.streamSource.hasAttribute("connected")) this.syncClaims()
  }

  connectionChanged() {
    if (this.streamSource?.hasAttribute("connected")) this.syncClaims()
  }

  visibilityChanged() {
    this.refreshContactTimes()
    this.scheduleContactRefresh()
    if (document.visibilityState === "visible") this.syncClaims()
  }

  rememberClaimExpiry(parentId, expiresAt) {
    const timestamp = expiresAt ? new Date(expiresAt).getTime() : NaN
    if (Number.isFinite(timestamp)) {
      this.claimExpiries.set(String(parentId), timestamp)
    } else {
      this.claimExpiries.delete(String(parentId))
    }
  }

  scheduleExpirySync() {
    window.clearTimeout(this.expiryTimer)
    const nextExpiry = Math.min(...this.claimExpiries.values())
    if (!Number.isFinite(nextExpiry)) return

    // The authoritative GET runs once only when the next lease would expire.
    const delay = Math.max(250, nextExpiry - Date.now() + 250)
    this.expiryTimer = window.setTimeout(this.syncClaims, Math.min(delay, 2_147_483_647))
  }

  loadComposer(url) {
    if (this.element.dataset.filtersPending === "true") return
    if (!url) return
    if (this.requiresSavedListValue) {
      this.dispatch("save-required")
      return
    }
    const frame = document.getElementById(this.composerFrameValue)
    if (!frame) return
    const absoluteUrl = new URL(url, window.location.origin).href
    if (frame.src === absoluteUrl) {
      frame.reload?.()
    } else {
      frame.src = url
    }
  }
}
